/**
 * Timeline chrome: month label, prev/next, zoom steppers, weekend toggle, and a
 * horizontally scrollable day header strip. Measures viewport width and calls
 * {@link getTimelineLayout} so the grid below shares cell width and visible day list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ScheduleDayDetailsHoverCard from "./ScheduleDayDetailsHoverCard";
import { CascadeDropDown } from "../../../../components/ui";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import {
  formatMonthYear,
  formatShortMonthDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import {
  ChevronDoubleDown,
  ChevronDoubleRight,
  ChevronLeft,
  ChevronRight,
  LockClosed,
  ZoomIn,
  ZoomOut,
} from "../../../../components/icons/svg/outline";
import { SCHEDULE_ZOOM_ORDER } from "../constants";
import { isScheduleWeekGapDivider } from "../model/scheduleTimelineWeekBoundary";
import {
  BG_SURFACE,
  BORDER_THICKNESS_STANDARD,
  HOVER_BG_PRIMARY_2,
  SCHEDULE_HEADER_CELL_BG,
  SCHEDULE_HEADER_CELL_BORDER,
  SCHEDULE_HEADER_CELL_BORDER_THICKNESS,
  SCHEDULE_HEADER_PANEL_DIVIDER_BORDER_THICKNESS,
  SCHEDULE_TIMELINE_LOCKED_DAY,
} from "../style";
import ScheduleToolbar from "../ScheduleToolbar";
import { getTimelineLayout } from "./timelineLayout";

/**
 * Timeline chrome for the schedule grid header strip.
 *
 * @param {Object} props
 * @param {Date} props.startDate Left edge of the visible day strip.
 * @param {number} props.zoomLevel Schedule zoom level (1–3).
 * @param {boolean} props.showWeekends When false, weekend columns are hidden.
 * @param {() => void} props.onPrev Move timeline one step backward.
 * @param {() => void} props.onNext Move timeline one step forward.
 * @param {() => void} props.onToday Reset timeline to the default current-week start.
 * @param {(level: number) => void} props.onZoomChange
 * @param {(layout: Object) => void} props.onLayoutChange Receives {@link getTimelineLayout} output.
 * @param {number} [props.horizontalScrollLeft] Shared scroll offset with the grid.
 * @param {(left: number) => void} [props.onHorizontalScroll]
 * @param {number} [props.leftPanelWidth] Width of the jobs/groups column (px).
 * @param {string} [props.className] Extra classes on the root element.
 * @param {(dateKey: string) => boolean} [props.isScheduleDayLocked]
 * @param {(dateKey: string) => Promise<void>|void} [props.onUnlockScheduleDay]
 * @param {(showWeekends: boolean) => void} [props.onShowWeekendsChange]
 * @param {boolean} [props.showToolbar=true]
 * @param {boolean} [props.showZoomControls=true]
 * @param {boolean} [props.showTreeControls=true]
 * @param {boolean} [props.showDayDetails=true]
 * @param {boolean} [props.scheduleTreeFullyExpanded]
 * @param {boolean} [props.scheduleExpandCollapseAllDisabled]
 * @param {() => void} [props.onToggleScheduleExpandCollapseAll]
 * @param {boolean} [props.datesPending=false] Hide date chrome while initial load resolves the timeline anchor.
 * @returns {JSX.Element}
 */
export default function TimelineHeader({
  startDate,
  zoomLevel,
  showWeekends,
  onPrev,
  onNext,
  onToday,
  onZoomChange,
  onLayoutChange,
  horizontalScrollLeft = 0,
  onHorizontalScroll,
  leftPanelWidth = 0,
  className = "",
  datesPending = false,
  isScheduleDayLocked = () => false,
  onUnlockScheduleDay,
  onShowWeekendsChange,
  showToolbar = true,
  showZoomControls = true,
  showTreeControls = true,
  showDayDetails = true,
  scheduleTreeFullyExpanded = false,
  scheduleExpandCollapseAllDisabled = true,
  onToggleScheduleExpandCollapseAll,
}) {
  const viewportRef = useRef(null);
  const lastLayoutSignatureRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(1000);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [dayCardPos, setDayCardPos] = useState(null);
  const dayCardOpenTimeoutRef = useRef(null);
  const dayCardCloseTimeoutRef = useRef(null);
  const [lockedDayContextMenu, setLockedDayContextMenu] = useState(null);

  /**
   * Cancel a pending day-details hover open.
   *
   * @param {void}
   * @returns {void}
   */
  const clearDayCardOpenTimeout = useCallback(() => {
    if (dayCardOpenTimeoutRef.current) {
      window.clearTimeout(dayCardOpenTimeoutRef.current);
      dayCardOpenTimeoutRef.current = null;
    }
  }, []);

  /**
   * Cancel a pending day-details hover close.
   *
   * @param {void}
   * @returns {void}
   */
  const clearDayCardCloseTimeout = useCallback(() => {
    if (dayCardCloseTimeoutRef.current) {
      window.clearTimeout(dayCardCloseTimeoutRef.current);
      dayCardCloseTimeoutRef.current = null;
    }
  }, []);

  /**
   * Hide the day-details hover card immediately.
   *
   * @param {void}
   * @returns {void}
   */
  const closeDayCard = useCallback(() => {
    clearDayCardOpenTimeout();
    clearDayCardCloseTimeout();
    setHoveredDay(null);
    setDayCardPos(null);
  }, [clearDayCardOpenTimeout, clearDayCardCloseTimeout]);

  /**
   * Delay-close the day-details card so the pointer can move onto it.
   *
   * @param {void}
   * @returns {void}
   */
  const scheduleDayCardClose = useCallback(() => {
    clearDayCardOpenTimeout();
    clearDayCardCloseTimeout();
    dayCardCloseTimeoutRef.current = window.setTimeout(closeDayCard, 120);
  }, [clearDayCardOpenTimeout, clearDayCardCloseTimeout, closeDayCard]);

  /**
   * Delay-open the day-details card above the hovered header cell.
   *
   * @param {Date} day Hovered calendar day.
   * @param {DOMRect} anchorRect Header-cell getBoundingClientRect.
   * @returns {void}
   */
  const scheduleDayCardOpen = useCallback(
    (day, anchorRect) => {
      clearDayCardOpenTimeout();
      clearDayCardCloseTimeout();
      dayCardOpenTimeoutRef.current = window.setTimeout(() => {
        setHoveredDay(day);
        setDayCardPos({
          top: anchorRect.top,
          left: anchorRect.left + anchorRect.width / 2,
        });
      }, 250);
    },
    [clearDayCardOpenTimeout, clearDayCardCloseTimeout]
  );

  useEffect(
    () => () => {
      clearDayCardOpenTimeout();
      clearDayCardCloseTimeout();
    },
    [clearDayCardOpenTimeout, clearDayCardCloseTimeout]
  );

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry?.contentRect?.width) {
        setViewportWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);
    setViewportWidth(node.getBoundingClientRect().width || 1000);

    return () => observer.disconnect();
  }, []);

  const { cellWidth, visibleDays, monthGroups, weekGroups, timelineWidth } =
    useMemo(
      () =>
        getTimelineLayout({
          startDate,
          zoomLevel,
          showWeekends,
          viewportWidth,
        }),
      [startDate, zoomLevel, showWeekends, viewportWidth]
    );

  const endDate = visibleDays[visibleDays.length - 1] || startDate;

  useEffect(() => {
    if (!onLayoutChange || !visibleDays.length) {
      return;
    }
    const firstKey = toLocalDateKey(visibleDays[0]);
    const lastKey = toLocalDateKey(visibleDays[visibleDays.length - 1]);
    const signature = `${firstKey}|${lastKey}|${cellWidth}|${timelineWidth}`;
    if (signature === lastLayoutSignatureRef.current) {
      return;
    }
    lastLayoutSignatureRef.current = signature;
    onLayoutChange({ cellWidth, visibleDays, timelineWidth });
  }, [cellWidth, visibleDays, timelineWidth, onLayoutChange]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) {
      return;
    }
    if (Math.abs(node.scrollLeft - horizontalScrollLeft) > 1) {
      node.scrollLeft = horizontalScrollLeft;
    }
  }, [horizontalScrollLeft]);

  const zoomIndex = SCHEDULE_ZOOM_ORDER.indexOf(zoomLevel);
  const safeZoomIndex = zoomIndex >= 0 ? zoomIndex : 0;

  /**
   * Step one zoom level coarser (more days, narrower cells).
   *
   * @param {void}
   * @returns {void}
   */
  const zoomOutOne = useCallback(() => {
    if (safeZoomIndex <= 0) {
      return;
    }
    onZoomChange(SCHEDULE_ZOOM_ORDER[safeZoomIndex - 1]);
  }, [onZoomChange, safeZoomIndex]);

  /**
   * Step one zoom level finer (fewer days, wider cells).
   *
   * @param {void}
   * @returns {void}
   */
  const zoomInOne = useCallback(() => {
    if (safeZoomIndex >= SCHEDULE_ZOOM_ORDER.length - 1) {
      return;
    }
    onZoomChange(SCHEDULE_ZOOM_ORDER[safeZoomIndex + 1]);
  }, [onZoomChange, safeZoomIndex]);

  /**
   * Dismiss the locked-day unlock menu.
   *
   * @param {void}
   * @returns {void}
   */
  const closeLockedDayContextMenu = useCallback(() => {
    setLockedDayContextMenu(null);
  }, []);

  /**
   * Open the unlock menu on a locked header cell.
   *
   * @param {import('react').MouseEvent} event Context-menu event.
   * @param {string} dateKey `YYYY-MM-DD`.
   * @returns {void}
   */
  const onLockedDayContextMenu = useCallback(
    (event, dateKey) => {
      if (!onUnlockScheduleDay) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const anchorRect = event.currentTarget?.getBoundingClientRect?.() ?? null;
      if (!anchorRect) {
        return;
      }
      setLockedDayContextMenu({ dateKey, anchorRect });
    },
    [onUnlockScheduleDay]
  );

  /**
   * Unlock-day action for the locked header-cell menu.
   *
   * @param {void}
   * @returns {Array<{ id: string, label: string, onSelect: Function }>}
   */
  const lockedDayContextItems = useMemo(
    () => [
      {
        id: "unlock-day",
        label: "Unlock Day",
        onSelect: () => {
          if (lockedDayContextMenu?.dateKey) {
            void onUnlockScheduleDay?.(lockedDayContextMenu.dateKey);
          }
        },
      },
    ],
    [lockedDayContextMenu, onUnlockScheduleDay]
  );

  return (
    <div
      className={`rounded border ${SCHEDULE_HEADER_CELL_BORDER} ${BG_SURFACE} ${className}`.trim()}
    >
      <div
        className={`flex h-[38px] min-w-0 items-center gap-2 border-b ${SCHEDULE_HEADER_CELL_BORDER} ${SCHEDULE_HEADER_CELL_BG} px-4`}
      >
        <div className="shrink-0 text-sm font-semibold text-gray-800">
          {datesPending
            ? "Loading…"
            : `${formatShortMonthDay(startDate)} - ${formatShortMonthDay(endDate)}`}
        </div>
        {showToolbar ? <ScheduleToolbar /> : <div className="min-w-0 flex-1" />}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {onShowWeekendsChange ? (
            <label className={`inline-flex h-6 items-center gap-1.5 rounded border ${BG_SURFACE} ${SCHEDULE_HEADER_CELL_BORDER} px-2 text-xs font-medium text-gray-700 ${HOVER_BG_PRIMARY_2}`}>
              <input
                type="checkbox"
                checked={showWeekends}
                onChange={(event) => onShowWeekendsChange(event.target.checked)}
                className="h-3 w-3 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Show Weekends
            </label>
          ) : null}
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous timeline range"
              title="Previous"
              className={`flex h-6 w-6 ${BG_SURFACE} shrink-0 items-center justify-center rounded border ${SCHEDULE_HEADER_CELL_BORDER} text-gray-700 ${HOVER_BG_PRIMARY_2}`}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onToday}
              className={`h-6 shrink-0 rounded border ${BG_SURFACE} ${SCHEDULE_HEADER_CELL_BORDER} px-2.5 text-xs font-medium text-gray-700 ${HOVER_BG_PRIMARY_2}`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={onNext}
              aria-label="Next timeline range"
              title="Next"
              className={`flex h-6 w-6 ${BG_SURFACE} shrink-0 items-center justify-center rounded border ${SCHEDULE_HEADER_CELL_BORDER} text-gray-700 ${HOVER_BG_PRIMARY_2}`}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {showZoomControls ? (
            <>
              <button
                type="button"
                aria-label="Decrease zoom level"
                title="Zoom out"
                disabled={safeZoomIndex >= SCHEDULE_ZOOM_ORDER.length - 1}
                onClick={zoomInOne}
                className={`flex h-6 w-6 ${BG_SURFACE} shrink-0 items-center justify-center rounded border ${SCHEDULE_HEADER_CELL_BORDER} text-gray-700 ${HOVER_BG_PRIMARY_2} disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <ZoomOut className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Increase zoom level"
                title="Zoom in"
                disabled={safeZoomIndex <= 0}
                onClick={zoomOutOne}
                className={`flex h-6 w-6 ${BG_SURFACE} shrink-0 items-center justify-center rounded border ${SCHEDULE_HEADER_CELL_BORDER} text-gray-700 ${HOVER_BG_PRIMARY_2} disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <ZoomIn className="h-4 w-4" aria-hidden />
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex min-w-0">
        {leftPanelWidth ? (
          <div
            className={`flex shrink-0 flex-col items-start justify-center gap-1 border-r-[${SCHEDULE_HEADER_PANEL_DIVIDER_BORDER_THICKNESS}px] ${SCHEDULE_HEADER_CELL_BORDER} ${SCHEDULE_HEADER_CELL_BG} px-2 py-1 pl-1 ${
              datesPending ? "opacity-0" : "opacity-100"
            }`}
            style={{ width: leftPanelWidth }}
            aria-hidden={datesPending}
          >
            <div className="flex w-full min-w-0 items-start ml-0.5">
              {showTreeControls ? (
                <button
                  type="button"
                  disabled={scheduleExpandCollapseAllDisabled || datesPending}
                  onClick={onToggleScheduleExpandCollapseAll}
                  className={`mr-1 mt-[1px] shrink-0 rounded text-gray-600 outline-none ${HOVER_BG_PRIMARY_2} focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-40`}
                  aria-label={
                    scheduleTreeFullyExpanded
                      ? PLANNING_TOOLTIP_MESSAGES.collapseAllProjectsAndGroups
                      : PLANNING_TOOLTIP_MESSAGES.expandAllProjectsAndGroups
                  }
                  title={
                    scheduleTreeFullyExpanded
                      ? PLANNING_TOOLTIP_MESSAGES.collapseAllProjectsAndGroups
                      : PLANNING_TOOLTIP_MESSAGES.expandAllProjectsAndGroups
                  }
                >
                  {scheduleTreeFullyExpanded ? (
                    <ChevronDoubleDown className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronDoubleRight className="h-4 w-4" aria-hidden />
                  )}
                </button>
              ) : null}
              <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-600">
                Projects / Groups
              </span>
            </div>
          </div>
        ) : null}
        <div
          ref={viewportRef}
          className={`min-w-0 flex-1 overflow-x-hidden ${
            datesPending ? "opacity-0" : "opacity-100"
          }`}
          onScroll={(e) => onHorizontalScroll?.(e.currentTarget.scrollLeft)}
        >
          <div style={{ minWidth: timelineWidth }}>
            <div className={`flex ${SCHEDULE_HEADER_CELL_BG}`}>
              {monthGroups.map((month) => (
                <div
                  key={month.key}
                  className={`border-b-[${SCHEDULE_HEADER_CELL_BORDER_THICKNESS}px] border-r-[${BORDER_THICKNESS_STANDARD}px] ${SCHEDULE_HEADER_CELL_BORDER} px-2 py-1 text-xs font-semibold text-gray-700`}
                  style={{ width: month.days * cellWidth }}
                >
                  {formatMonthYear(month.start, undefined, false)}
                </div>
              ))}
            </div>

            {zoomLevel !== 3 && (
              <div className={`relative ${SCHEDULE_HEADER_CELL_BG}`}>
                <div className="relative z-[1] flex min-w-0">
                  {weekGroups.map((week) => (
                    <div
                      key={week.key}
                      className={`truncate border-b-[${SCHEDULE_HEADER_CELL_BORDER_THICKNESS}px] border-r-[${BORDER_THICKNESS_STANDARD}px] ${SCHEDULE_HEADER_CELL_BORDER} px-2 py-1 text-[11px] text-gray-600`}
                      style={{ width: week.days * cellWidth }}
                    >
                      Week of {formatShortMonthDay(week.start)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex">
              {visibleDays.map((day, dayIdx) => {
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isSaturday = day.getDay() === 6;
                const dayKey = toLocalDateKey(day);
                const nextDay =
                  dayIdx < visibleDays.length - 1
                    ? visibleDays[dayIdx + 1]
                    : null;
                const isSaturdayToSundayDivider =
                  showWeekends &&
                  isSaturday &&
                  nextDay != null &&
                  nextDay.getDay() === 0;
                const headerWeekGapAfter =
                  nextDay != null &&
                  isScheduleWeekGapDivider(day, nextDay);
                const isDayCardActive =
                  hoveredDay != null && toLocalDateKey(hoveredDay) === dayKey;
                const dayRightBorderThickness =
                  headerWeekGapAfter || isSaturdayToSundayDivider
                    ? BORDER_THICKNESS_STANDARD
                    : SCHEDULE_HEADER_CELL_BORDER_THICKNESS;
                return (
                  <div
                    key={dayKey}
                    className={`shrink-0 cursor-default hover:bg-primary/15 ${isDayCardActive ? "!bg-primary/15" : ""} border-r-[${dayRightBorderThickness}px] ${SCHEDULE_HEADER_CELL_BORDER} ${zoomLevel === 3 ? "px-0.5 py-1.5" : "px-1 py-1"
                      } text-center text-[11px] ${isWeekend
                        ? `${SCHEDULE_HEADER_CELL_BG} text-gray-500`
                        : "text-gray-700"
                      } ${SCHEDULE_HEADER_CELL_BG}`}
                    style={{ width: cellWidth, minWidth: cellWidth }}
                    onMouseEnter={(e) => {
                      if (!showDayDetails) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      scheduleDayCardOpen(day, rect);
                    }}
                    onMouseLeave={showDayDetails ? scheduleDayCardClose : undefined}
                  >
                    <div
                      className={`${zoomLevel === 3 ? "text-[0.5rem]" : "text-xs"
                        }`}
                    >
                      {day.toLocaleDateString(undefined, {
                        weekday: "short",
                      })}
                    </div>
                    <div className="flex flex-row items-center justify-center font-medium">
                      <span>{day.getDate()}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* One header cell per visible day: padlock when Dispatch marks the date locked (DayDetails); same widths/borders as weekday row above. */}
            <div className="flex">
              {visibleDays.map((day, dayIdx) => {
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isSaturday = day.getDay() === 6;
                const dayKey = toLocalDateKey(day);
                const locked = showDayDetails && isScheduleDayLocked(dayKey);
                const nextDay =
                  dayIdx < visibleDays.length - 1
                    ? visibleDays[dayIdx + 1]
                    : null;
                const isSaturdayToSundayDivider =
                  showWeekends &&
                  isSaturday &&
                  nextDay != null &&
                  nextDay.getDay() === 0;
                const headerWeekGapAfter =
                  nextDay != null &&
                  isScheduleWeekGapDivider(day, nextDay);
                const dayRightBorderThickness =
                  headerWeekGapAfter || isSaturdayToSundayDivider
                    ? BORDER_THICKNESS_STANDARD
                    : SCHEDULE_HEADER_CELL_BORDER_THICKNESS;
                const lockRowTone =
                  `${SCHEDULE_HEADER_CELL_BG}${locked && SCHEDULE_TIMELINE_LOCKED_DAY.surfaceBgClass
                      ? ` ${SCHEDULE_TIMELINE_LOCKED_DAY.surfaceBgClass}`
                      : ""
                    } ${locked ? "" : isWeekend ? "text-gray-500" : "text-gray-700"}`.trim();
                const lockIconFrame =
                  zoomLevel === 3 ? "h-2 w-2" : "h-2.5 w-2.5";
                return (
                  <div
                    key={`${dayKey}-lock`}
                    title={
                      locked
                        ? PLANNING_TOOLTIP_MESSAGES.dayLockedDueToDispatchReview
                        : undefined
                    }
                    className={`flex min-h-[14px] shrink-0 items-center justify-center border-r-[${dayRightBorderThickness}px] border-t-[${SCHEDULE_HEADER_CELL_BORDER_THICKNESS}px] ${SCHEDULE_HEADER_CELL_BORDER} ${lockRowTone}${locked ? " cursor-default" : ""}`}
                    style={{ width: cellWidth, minWidth: cellWidth }}
                    onContextMenu={(event) => {
                      if (showDayDetails && locked) {
                        onLockedDayContextMenu(event, dayKey);
                      }
                    }}
                  >
                    {locked ? (
                      <LockClosed
                        className={`shrink-0 text-gray-500 ${lockIconFrame}`}
                        aria-hidden
                      />
                    ) : (
                      <span
                        className={`inline-block shrink-0 ${lockIconFrame}`}
                        aria-hidden
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {showDayDetails && hoveredDay && dayCardPos ? (
        <ScheduleDayDetailsHoverCard
          day={hoveredDay}
          top={dayCardPos.top}
          left={dayCardPos.left}
          onMouseEnter={clearDayCardCloseTimeout}
          onMouseLeave={scheduleDayCardClose}
        />
      ) : null}
      {showDayDetails ? (
        <CascadeDropDown
          open={Boolean(lockedDayContextMenu)}
          onClose={closeLockedDayContextMenu}
          anchorRect={lockedDayContextMenu?.anchorRect ?? null}
          items={lockedDayContextItems}
        />
      ) : null}
    </div>
  );
}

